/*
 * Fernwartung über Mesh an einem Touchpanel (hvp-contracts,
 * docs/wire-remote-session.md): Sitzung öffnen (RemoteSessionDialog),
 * laufende in drei Schritten sehen, verlängern, beenden. Der Server rechnet
 * die Freigabe und unterschreibt sie; hier wird nur gewählt, was der
 * Techniker erreichen will.
 *
 * Verbunden wird nur über den Technician Mesh Client (hvacloud-fw), mit
 * einem Einmal-Ticket im Link. Ein installiertes Tailscale ist kein Weg
 * (Entscheidung Patrick, 04.11.2026). Geöffnet wird hier, nicht im Client.
 */
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { StatusBadge } from './ui/StatusBadge';
import { RemoteSessionDialog } from './RemoteSessionDialog';
import {
  CLIENT_START_GRACE_MS, helperLink, restText, ruleAction, rulesByTarget, serviceLabel, sessionSteps, showDownload,
  type HelperPort, type TargetSuggestion, type OpenBody, type Rule, type RuleAction, type Service, type StepState,
} from './remoteSessionModel';

interface Session {
  id: string;
  mine: boolean;
  requestedBy: string;
  mode: 'read' | 'write';
  status: 'requested' | 'active' | 'ended';
  reason: string;
  startedAt: string;
  expiresAt: string;
  hardLimitAt: string;
  endedAt: string | null;
  endedReason: string | null;
  tailnetAddress: string | null;
  nodeName: string | null;
  technicianJoined: boolean;
  rules: Rule[];
  helperConnected: boolean;
  helperPorts: HelperPort[];
  helperStarted: boolean;
}

interface ClientState {
  ready: boolean;
  missing: string[];
  canWrite: boolean;
  canOpen: boolean;
  busyByOther: boolean;
  sessions: Session[];
}

interface Omission {
  host: string | null;
  serviceKey: string;
  reason: string;
}

interface OpenResponse {
  session: Session;
  omissions: Omission[];
}

/** Wo der Technician Mesh Client für Windows liegt (Velopack, wie Outpost). */
const HELPER_DOWNLOAD = 'https://updates.sinnograte.at/fw-helfer/hvacloud-fw-setup.exe';

/** Merkt sich je Browser, dass hier schon ein Mesh Client verbunden war. */
const SEEN_KEY = 'hvacloud.meshClientSeen';

function seenBefore(): boolean {
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return false; }
}

function markSeen() {
  try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* privates Fenster: dann eben jedes Mal anbieten */ }
}

/** Laufzeit einer frischen Sitzung; der Server legt sie fest (RemoteAccess:SessionMinutes). */
const SESSION_MINUTES = 30;

const MISSING: Record<string, string> = {
  disabled: 'Die Fernwartung ist auf diesem Server ausgeschaltet.',
  headscale_not_configured: 'Headscale ist auf diesem Server nicht eingerichtet.',
  no_server_key: 'Auf diesem Server fehlt der Schlüssel für Fernwartungssitzungen.',
  no_capability: 'Das Panel meldet keine Fernwartung über Mesh. Sie braucht den Schalter FERNWARTUNG im TP-Configurator und eine aktuelle App.',
  no_device_key: 'Für das Panel ist noch kein Geräteschlüssel hinterlegt; er kommt mit der nächsten Zertifikatserneuerung.',
};

const ENDED: Record<string, string> = {
  user: 'beendet',
  admin: 'von einem Administrator beendet',
  expired: 'abgelaufen',
  hard_limit: 'Obergrenze von 4 h erreicht',
  activation_timeout: 'Panel hat nicht rechtzeitig geantwortet',
  device_lost: 'Verbindung zum Panel verloren',
  key_reuse: 'Beitrittsschlüssel zweimal abgerufen, aus Sicherheitsgründen beendet',
  server: 'vom Server beendet',
  'device:local': 'am Panel beendet',
  'device:expired': 'am Panel abgelaufen',
  'device:heartbeat': 'am Panel beendet, ohne Lebenszeichen der Cloud',
  'device:not_running': 'am Panel nicht mehr aktiv',
  'device:not_enabled': 'am Panel ist die Fernwartung ausgeschaltet',
  'device:busy': 'am Panel läuft schon eine andere Sitzung',
  'device:not_ready': 'tailscale lädt am Panel noch',
};

const OPEN_ERRORS: Record<string, string> = {
  client_busy: 'Am Panel läuft schon eine Sitzung.',
  user_busy: 'Du hast schon eine offene Sitzung an einem anderen Gerät.',
  writer_busy: 'An dieser Anlage arbeitet schon jemand schreibend.',
  no_capability: MISSING.no_capability,
  no_device_key: MISSING.no_device_key,
  no_server_key: MISSING.no_server_key,
  disabled: MISSING.disabled,
  reason_required: 'Bitte einen Grund angeben.',
};

const STEP_TONE: Record<StepState, 'ok' | 'info' | 'neutral'> = { done: 'ok', current: 'info', waiting: 'neutral' };

function endedText(reason: string | null): string {
  if (!reason) return 'beendet';
  return ENDED[reason] ?? (reason.startsWith('device:') ? `am Panel beendet (${reason.slice(7)})` : reason);
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { reason?: string; message?: string } | null;
    if (body?.reason && OPEN_ERRORS[body.reason]) return OPEN_ERRORS[body.reason];
    if (body?.message) return body.message;
    if (e.status === 403) return 'Dafür fehlt dir das Recht.';
    return `Fehler ${e.status}.`;
  }
  return 'Server nicht erreichbar.';
}

/** Kleine Drehung für "läuft gerade". */
function Spinner() {
  return <span class="hv-remote-session__spinner" role="status" aria-label="läuft" />;
}

function copy(text: string) {
  void navigator.clipboard?.writeText(text);
}

/** Eine Zeile je Dienst: Name, Adresse auf dem Rechner, Aktion. */
function ServiceRow({ rule, action }: { rule: Rule; action: RuleAction | null }) {
  let address: preact.ComponentChildren = <span class="muted">nach dem Verbinden</span>;
  let button: preact.ComponentChildren = null;
  if (action?.kind === 'web') {
    address = <code class="mono">{action.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</code>;
    button = <a class="hv-btn hv-btn--sm" href={action.url} target="_blank" rel="noopener noreferrer">Öffnen</a>;
  } else if (action?.kind === 'scope') {
    address = (
      <>
        <code class="mono">{action.host}:{action.port}</code>
        {action.apiPort !== null && <span class="muted"> · Device API {action.apiPort}</span>}
      </>
    );
    button = <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => copy(action.host)}>Host kopieren</button>;
  } else if (action?.kind === 'copy') {
    address = <code class="mono">{action.text}</code>;
    button = <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => copy(action.text)}>Kopieren</button>;
  }
  return (
    <tr>
      <td>{serviceLabel(rule.serviceKey)}</td>
      <td>{address}</td>
      <td class="hv-remote-session__act">{button}</td>
    </tr>
  );
}

export function RemoteSessionPanel({ plantId, clientId, clientLabel }: { plantId: string; clientId: string; clientLabel?: string }) {
  const base = `/api/plants/${plantId}/clients/${clientId}/remote-sessions`;
  const [state, setState] = useState<ClientState | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [omissionsFor, setOmissionsFor] = useState<{ sessionId: string; list: Omission[] } | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [clickedAt, setClickedAt] = useState<number | null>(null);
  const [seen, setSeen] = useState(seenBefore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  async function reload() {
    try {
      setState(await api.get<ClientState>(base));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e));
    }
  }

  useEffect(() => {
    void reload();
    api.get<Service[]>('/api/remote-access/services').then(setServices).catch(() => setServices([]));
  }, [plantId, clientId]);

  const open = state?.sessions.find((s) => s.status !== 'ended') ?? null;

  useEffect(() => {
    if (open?.helperConnected && !seen) {
      markSeen();
      setSeen(true);
    }
  }, [open?.helperConnected]);

  // Gerätenamen für die Überschriften, aus derselben Liste wie im Dialog.
  useEffect(() => {
    if (!open) return;
    api.get<TargetSuggestion[]>(`/api/plants/${plantId}/clients/${clientId}/remote-targets`)
      .then((t) => setNames(Object.fromEntries(t.filter((x) => x.name).map((x) => [x.host, x.name!]))))
      .catch(() => setNames({}));
  }, [open?.id]);

  // Während eine Sitzung läuft, Zustand nachladen und die Restzeit zählen.
  // Bis der Techniker verbunden ist, öfter: darauf wartet jemand.
  useEffect(() => {
    if (!open) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const fast = open.status === 'requested' || !open.helperConnected;
    const poll = setInterval(() => void reload(), fast ? 3000 : 15000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [open?.id, open?.status, open?.helperConnected]);

  // "Verbinden": Einmal-Ticket holen und gleich an den Mesh Client geben. Kein
  // gewöhnlicher Link, weil das Ticket erst beim Klick entstehen darf.
  async function connect(id: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ ticket: string }>(`/api/remote-sessions/${id}/helper-ticket`);
      setClickedAt(Date.now());
      window.location.href = helperLink(window.location.host, id, res.ticket);
      // Nach der Wartezeit einmal nachladen, damit "nicht gestartet" auch
      // ohne die nächste Abfrage erscheint.
      setTimeout(() => void reload(), CLIENT_START_GRACE_MS + 500);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submit(body: OpenBody) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<OpenResponse>(base, body);
      setOmissionsFor({ sessionId: res.session.id, list: res.omissions });
      setDialogOpen(false);
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function act(kind: 'extend' | 'end', id: string) {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'extend') await api.post(`/api/remote-sessions/${id}/extend`);
      else await api.delete(`/api/remote-sessions/${id}`);
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <p class="muted">{loadError}</p>;
  if (!state) return <p class="muted">Lade Fernwartung…</p>;

  if (open) {
    const omissions = omissionsFor?.sessionId === open.id ? omissionsFor.list : [];
    const joined = open.helperConnected || open.technicianJoined;
    const [s1, s2, s3] = sessionSteps(open.status, joined);
    const download = showDownload(seen, clickedAt, open.helperStarted || joined, now);
    const canConnect = open.status === 'active' && !busy;
    const canExtend = open.mine && open.status === 'active'
      && new Date(open.expiresAt).getTime() < new Date(open.hardLimitAt).getTime();
    return (
      <div class="hv-remote-session">
        <p>
          {open.mode === 'write' ? 'Schreibend' : 'Lesend'}, noch <b>{restText(open.expiresAt, now)}</b>
          {!open.mine && <span class="muted"> · {open.requestedBy}</span>}
        </p>
        <p class="muted">Grund: {open.reason}</p>

        <ol class="hv-remote-session__steps">
          <li class={`is-${s1}`}>
            <StatusBadge tone={STEP_TONE[s1]}>1</StatusBadge>{' '}
            <b>Panel verbindet</b>{' '}
            {open.status === 'requested'
              ? <span class="muted"><Spinner /> Panel tritt dem Tailnet bei…</span>
              : <span class="muted">im Tailnet als <code class="mono">{open.tailnetAddress}</code>{open.nodeName && ` (${open.nodeName})`}</span>}
          </li>
          <li class={`is-${s2}`}>
            <StatusBadge tone={STEP_TONE[s2]}>2</StatusBadge>{' '}
            <b>Techniker verbinden</b>{' '}
            {joined
              ? <span class="muted">Technician Mesh Client verbunden</span>
              : open.mine && (
                <span class="hv-remote-session__connect">
                  <button type="button" class="hv-btn hv-btn--sm hv-btn--primary" disabled={!canConnect}
                          title={open.status === 'active' ? undefined : 'Erst wenn das Panel verbunden ist'}
                          onClick={() => void connect(open.id)}>Verbinden</button>
                  {download === 'offer' && (
                    <a class="hv-btn hv-btn--sm hv-btn--ghost" href={HELPER_DOWNLOAD} target="_blank" rel="noopener noreferrer">
                      Technician Mesh Client herunterladen
                    </a>
                  )}
                </span>
              )}
            {open.mine && !joined && (open.helperStarted || (clickedAt !== null && download !== 'missing')) && (
              <p class="muted">
                <Spinner /> {open.helperStarted ? 'Technician Mesh Client tritt bei…' : 'Technician Mesh Client startet…'}
              </p>
            )}
            {open.mine && !joined && !open.helperStarted && download === 'missing' && (
              <p class="hv-remote-session__error">
                Der Technician Mesh Client hat sich nicht gemeldet. Noch nicht installiert?{' '}
                <a href={HELPER_DOWNLOAD} target="_blank" rel="noopener noreferrer">Herunterladen</a>, einrichten, dann erneut „Verbinden“.
              </p>
            )}
            {open.mine && !joined && !open.helperStarted && clickedAt === null && (
              <p class="muted">
                {open.status === 'active'
                  ? '„Verbinden“ startet den Technician Mesh Client. Er legt jedes Gerät auf deinem Rechner ab; geöffnet wird hier.'
                  : 'Verbinden geht, sobald das Panel im Tailnet ist.'}
              </p>
            )}
          </li>
          <li class={`is-${s3}`}>
            <StatusBadge tone={STEP_TONE[s3]}>3</StatusBadge>{' '}
            <b>Arbeiten</b>
            {open.rules.length > 0 ? rulesByTarget(open.rules).map((g) => (
              <details class="hv-remote-session__device" open key={g.host}>
                <summary>
                  <b>{names[g.host] ?? g.host}</b>
                  {names[g.host] && <span class="muted mono"> {g.host}</span>}
                </summary>
                <table class="hv-remote-session__rules">
                  <tbody>
                    {g.rules.map((r) => (
                      <ServiceRow key={r.listenPort} rule={r}
                                  action={joined ? ruleAction(r, open.rules, open.helperPorts) : null} />
                    ))}
                  </tbody>
                </table>
              </details>
            )) : <p class="muted">Keine Weiterleitung an LAN2, nur das Panel selbst.</p>}
          </li>
        </ol>

        {omissions.length > 0 && (
          <ul class="hv-remote-session__omissions">
            {omissions.map((o) => <li key={`${o.host}-${o.serviceKey}`}>{o.host} {o.serviceKey}: {o.reason}</li>)}
          </ul>
        )}

        <p class="hv-remote-session__actions">
          {canExtend && (
            <button type="button" class="hv-btn hv-btn--sm" disabled={busy} onClick={() => void act('extend', open.id)}>
              Um 30 min verlängern
            </button>
          )}
          <button type="button" class="hv-btn hv-btn--sm hv-btn--danger-outline" disabled={busy}
                  onClick={() => void act('end', open.id)}>Beenden</button>
        </p>
        {error && <p class="hv-remote-session__error">{error}</p>}
      </div>
    );
  }

  const last = state.sessions[0];
  if (!state.ready) {
    return (
      <div class="hv-remote-session">
        {state.missing.map((m) => <p key={m} class="muted">{MISSING[m] ?? m}</p>)}
        {last && <p class="muted">Letzte Sitzung: {endedText(last.endedReason)}</p>}
      </div>
    );
  }
  if (!state.canOpen) return <p class="muted">Dafür fehlt dir das Recht remote.session.read.</p>;
  return (
    <div class="hv-remote-session">
      {last && <p class="muted">Letzte Sitzung: {endedText(last.endedReason)}, {new Date(last.startedAt).toLocaleString('de-AT')}</p>}
      <button type="button" class="hv-btn hv-btn--sm hv-btn--primary"
              onClick={() => { setError(null); setDialogOpen(true); }}>Fernwartung starten</button>
      {dialogOpen && (
        <RemoteSessionDialog
          plantId={plantId} clientId={clientId} clientLabel={clientLabel ?? 'Touchpanel'}
          services={services} canWrite={state.canWrite} busyByOther={state.busyByOther}
          sessionMinutes={SESSION_MINUTES} busy={busy} error={error}
          onSubmit={(b) => void submit(b)} onClose={() => setDialogOpen(false)} />
      )}
    </div>
  );
}
